import { useLlmoComparisonApi } from '@/common/api/modules/llmo'
import { QueryFanoutResult } from '@/common/model/media/queryFanout'
import { signedFixed } from '@/common/util/number'

import Text, { TextColor } from '@/components/view/atoms/text/Text'
import InsideArea, { BgColor } from '@/components/view/layouts/area/InsideArea'

import GeoScoreCard from '@/components/container/modules/mediaGeo/common/GeoScoreCard'
import GeoSection from '@/components/container/modules/mediaGeo/common/GeoSection'

import QueryFanoutConversationFlow from './QueryFanoutConversationFlow'
import QueryFanoutDepthTable from './QueryFanoutDepthTable'
import QueryFanoutInsightsPanel from './QueryFanoutInsightsPanel'
import QueryFanoutRetentionChart from './QueryFanoutRetentionChart'

interface Props {
  result: QueryFanoutResult
  siteId: number
  historyId: number
}

const hasInsights = (insights: QueryFanoutResult['insights']): boolean =>
  !!insights &&
  (!!insights.strongest_stage || !!insights.weakest_stage || !!insights.recommendation)

// クエリ・ファンアウト分析の結果ビュー全体
const QueryFanoutResultView = ({ result, siteId, historyId }: Props) => {
  // 前回結果との比較（前回値・変化量）。比較対象が無い場合は available=false
  const { data: comparison } = useLlmoComparisonApi({ site_id: siteId, history_id: historyId })
  const diff = comparison?.available ? comparison.diff : undefined

  const retentionChart = result.retention_chart ?? []
  const depthAnalysis = result.depth_analysis ?? []

  return (
    <div className='flex flex-col'>
      {/* 深度別リテンション（棒グラフ＋持続スコア・深度別詳細） */}
      <GeoSection
        title='深度別リテンション'
        subText='会話深度ごとの競合ブランド数と自社ブランドの存在'
        noPadding
      >
        <div className='grid grid-cols-1 lg:grid-cols-2'>
          <div className='p-4'>
            <QueryFanoutRetentionChart retentionChart={retentionChart} />
          </div>
          <div className='border-secondary flex flex-col gap-3 border-l'>
            <div className='p-4'>
              <GeoScoreCard
                title='ブランド持続スコア'
                value={(result.persistence_score ?? 0).toFixed(2)}
                unit='/1.00'
                previous={
                  diff?.previous_persistence != null ? diff.previous_persistence.toFixed(2) : null
                }
                change={
                  diff?.persistence_change != null ? signedFixed(diff.persistence_change) : null
                }
              />
            </div>
            {depthAnalysis.length > 0 && (
              <QueryFanoutDepthTable
                depthAnalysis={depthAnalysis}
                retentionChart={retentionChart}
              />
            )}
            {result.dropout_point && (
              <InsideArea color={BgColor.CAUTION} type='thin'>
                <div className='flex flex-col gap-0.5'>
                  <Text size={12} weight='bold' color={TextColor.CRITICAL}>
                    脱落ポイント
                  </Text>
                  <Text size={12} color={TextColor.SECONDARY} lineHeight={150}>
                    {result.dropout_point}
                  </Text>
                </div>
              </InsideArea>
            )}
          </div>
        </div>
      </GeoSection>

      <QueryFanoutConversationFlow flow={result.conversation_flow ?? []} />

      {hasInsights(result.insights) && result.insights && (
        <QueryFanoutInsightsPanel insights={result.insights} />
      )}
    </div>
  )
}

export default QueryFanoutResultView
